<template>
  <div
    ref="root"
    :class="['zoomable-image', { 'zoomable-image--fullscreen': isFullscreen, 'zoomable-image--pseudo': isPseudoFullscreen }]"
    tabindex="0"
    @keydown="onKeydown"
  >
    <div
      ref="stage"
      :class="['zoomable-image__stage', { 'zoomable-image__stage--zoomed': scale > 1, 'zoomable-image__stage--dragging': isDragging }]"
      @wheel.prevent="onWheel"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @dblclick="onDoubleClick"
    >
      <img
        :src="src"
        :alt="caption"
        draggable="false"
        class="zoomable-image__img"
        :style="{ transform: `translate(${x}px, ${y}px) scale(${scale})` }"
      />
    </div>

    <div v-if="caption && isAnyFullscreen" class="zoomable-image__caption">{{ caption }}</div>

    <template v-if="hasPrevious || hasNext">
      <v-btn
        v-if="hasPrevious"
        class="zoomable-image__nav zoomable-image__nav--previous"
        icon="mdi-chevron-left"
        size="small"
        title="Image précédente"
        @click="$emit('previous')"
      ></v-btn>
      <v-btn
        v-if="hasNext"
        class="zoomable-image__nav zoomable-image__nav--next"
        icon="mdi-chevron-right"
        size="small"
        title="Image suivante"
        @click="$emit('next')"
      ></v-btn>
    </template>

    <div class="zoomable-image__controls">
      <v-btn icon="mdi-magnify-minus-outline" size="small" variant="text" title="Zoom arrière" :disabled="scale <= minScale" @click="zoomBy(1 / 1.5)"></v-btn>
      <v-btn icon="mdi-magnify-plus-outline" size="small" variant="text" title="Zoom avant" :disabled="scale >= maxScale" @click="zoomBy(1.5)"></v-btn>
      <v-btn icon="mdi-fit-to-screen-outline" size="small" variant="text" title="Ajuster à l'écran" :disabled="scale === 1" @click="reset"></v-btn>
      <v-btn
        :icon="isAnyFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen'"
        size="small"
        variant="text"
        :title="isAnyFullscreen ? 'Quitter le plein écran' : 'Plein écran'"
        @click="toggleFullscreen"
      ></v-btn>
    </div>
  </div>
</template>

<script>
  // Image viewer with zoom (wheel, pinch, double click, buttons), drag to pan and fullscreen.
  // The image fills the stage with object-fit: contain; zooming scales it around the pointer.
  export default {
    name: "ZoomableImage",
    props: {
      src: {
        type: String,
        required: true,
      },
      caption: {
        type: String,
        default: "",
      },
      hasPrevious: {
        type: Boolean,
        default: false,
      },
      hasNext: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["previous", "next"],
    data() {
      return {
        minScale: 1,
        maxScale: 6,
        scale: 1,
        x: 0,
        y: 0,
        pointers: new Map(),
        lastPinchDistance: null,
        isDragging: false,
        isFullscreen: false,
        // Fallback where the Fullscreen API isn't available for elements (iPhone Safari)
        isPseudoFullscreen: false,
      };
    },
    computed: {
      isAnyFullscreen() {
        return this.isFullscreen || this.isPseudoFullscreen;
      },
    },
    watch: {
      src() {
        this.reset();
      },
    },
    mounted() {
      document.addEventListener("fullscreenchange", this.onFullscreenChange);
    },
    beforeUnmount() {
      document.removeEventListener("fullscreenchange", this.onFullscreenChange);
      document.body.style.overflow = "";
    },
    methods: {
      reset() {
        this.scale = 1;
        this.x = 0;
        this.y = 0;
      },
      // Zooms to newScale keeping the stage point (px, py) under the same image point
      zoomAt(newScale, px, py) {
        const scale = Math.min(this.maxScale, Math.max(this.minScale, newScale));
        const ratio = scale / this.scale;
        this.x = px - (px - this.x) * ratio;
        this.y = py - (py - this.y) * ratio;
        this.scale = scale;
        this.clamp();
      },
      zoomBy(factor) {
        const { width, height } = this.$refs.stage.getBoundingClientRect();
        this.zoomAt(this.scale * factor, width / 2, height / 2);
      },
      // Keeps the zoomed image covering the stage so it can't be dragged away
      clamp() {
        const { width, height } = this.$refs.stage.getBoundingClientRect();
        this.x = Math.min(0, Math.max(width - width * this.scale, this.x));
        this.y = Math.min(0, Math.max(height - height * this.scale, this.y));
      },
      stagePoint(event) {
        const rect = this.$refs.stage.getBoundingClientRect();
        return { px: event.clientX - rect.left, py: event.clientY - rect.top };
      },
      onWheel(event) {
        const { px, py } = this.stagePoint(event);
        this.zoomAt(this.scale * Math.exp(-event.deltaY * 0.0015), px, py);
      },
      onDoubleClick(event) {
        const { px, py } = this.stagePoint(event);
        if (this.scale > 1) {
          this.reset();
        } else {
          this.zoomAt(2.5, px, py);
        }
      },
      onPointerDown(event) {
        this.$refs.stage.setPointerCapture(event.pointerId);
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        this.isDragging = this.pointers.size === 1 && this.scale > 1;
        this.lastPinchDistance = null;
      },
      onPointerMove(event) {
        const previous = this.pointers.get(event.pointerId);
        if (!previous) {
          return;
        }
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (this.pointers.size === 2) {
          // Pinch: zoom by the change in distance between the two fingers, around their middle
          const [a, b] = [...this.pointers.values()];
          const distance = Math.hypot(a.x - b.x, a.y - b.y);
          const rect = this.$refs.stage.getBoundingClientRect();
          if (this.lastPinchDistance) {
            this.zoomAt(
              this.scale * (distance / this.lastPinchDistance),
              (a.x + b.x) / 2 - rect.left,
              (a.y + b.y) / 2 - rect.top,
            );
          }
          this.lastPinchDistance = distance;
        } else if (this.isDragging) {
          this.x += event.clientX - previous.x;
          this.y += event.clientY - previous.y;
          this.clamp();
        }
      },
      onPointerUp(event) {
        this.pointers.delete(event.pointerId);
        this.lastPinchDistance = null;
        this.isDragging = this.pointers.size === 1 && this.scale > 1;
      },
      onKeydown(event) {
        if (event.key === "ArrowLeft" && this.hasPrevious) this.$emit("previous");
        else if (event.key === "ArrowRight" && this.hasNext) this.$emit("next");
        else if (event.key === "+" || event.key === "=") this.zoomBy(1.5);
        else if (event.key === "-") this.zoomBy(1 / 1.5);
        else if (event.key === "Escape" && this.isPseudoFullscreen) this.setPseudoFullscreen(false);
      },
      async toggleFullscreen() {
        if (this.isPseudoFullscreen) {
          this.setPseudoFullscreen(false);
        } else if (document.fullscreenElement) {
          await document.exitFullscreen();
        } else if (this.$refs.root.requestFullscreen) {
          try {
            await this.$refs.root.requestFullscreen();
          } catch {
            this.setPseudoFullscreen(true);
          }
        } else {
          this.setPseudoFullscreen(true);
        }
        this.$refs.root.focus();
      },
      setPseudoFullscreen(value) {
        this.isPseudoFullscreen = value;
        document.body.style.overflow = value ? "hidden" : "";
        this.$nextTick(this.reset);
      },
      onFullscreenChange() {
        this.isFullscreen = document.fullscreenElement === this.$refs.root;
        this.$nextTick(this.reset);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .zoomable-image {
    position: relative;
    height: 100%;
    background: #111;
    border-radius: 8px;
    overflow: hidden;
    outline: none;
  }
  .zoomable-image--fullscreen,
  .zoomable-image--pseudo {
    border-radius: 0;
  }
  .zoomable-image--pseudo {
    position: fixed;
    inset: 0;
    z-index: 3000;
    height: auto;
  }
  .zoomable-image__stage {
    position: absolute;
    inset: 0;
    overflow: hidden;
    // Not zoomed: a vertical swipe still scrolls the page, pinches come to the viewer
    touch-action: pan-y;
    cursor: zoom-in;
  }
  .zoomable-image__stage--zoomed {
    // Zoomed: every touch gesture pans or zooms the image
    touch-action: none;
    cursor: grab;
  }
  .zoomable-image__stage--dragging {
    cursor: grabbing;
  }
  .zoomable-image__img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transform-origin: 0 0;
    user-select: none;
  }
  .zoomable-image__controls {
    position: absolute;
    right: 8px;
    bottom: 8px;
    display: flex;
    border-radius: 24px;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
  }
  .zoomable-image__nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(0, 0, 0, 0.55) !important;
    color: #fff !important;
  }
  .zoomable-image__nav--previous {
    left: 8px;
  }
  .zoomable-image__nav--next {
    right: 8px;
  }
  .zoomable-image__caption {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    padding: 12px 16px;
    color: #fff;
    background: linear-gradient(rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0));
    pointer-events: none;
  }
</style>
